import { reportBrokenField } from '@/lib/tolerant-rows';

import { isWithdrawableNowBasis } from './schemas';

/** The served liquidity the withdrawable now figures are read from. */
export interface WithdrawableNowTotalInput {
  basis: string;
  totalValueUsd: string;
  coveredValueUsd: string;
  uncoveredValueUsd: string;
  /** Positions held in all; past `positions.length` only on the ceiling. */
  totalPositions?: number;
  immediateValueUsd: { typical: string | null; worstCase: string | null };
  positions: readonly {
    vaultAddress: string;
    valueUsd: string | null;
    uncoveredReason?: string | null;
    withdrawableNow?: {
      /** The vault's own `maxWithdraw(owner)`, the read behind valueUsd. */
      maxWithdrawRaw?: bigint | null;
      valueUsd: string | null;
      reason: string | null;
    } | null;
  }[];
}

/** One priced position's answer, capped at the position as summed. */
interface AcceptedPosition {
  /** min(position value, withdrawable now), a decimal string. */
  withdrawableUsd: string;
  /** What waits (the strategist, or a reason not given), a decimal string. */
  waitUsd: string;
  /**
   * What leaving now costs (`exit_cost`: the withdraw fee and exit impact,
   * since the answer is net of them), a decimal string. A cost, not money
   * that waits.
   */
  costUsd: string;
  /** Nothing of the position can leave now. */
  none: boolean;
  /** All of it can leave now (within rounding), less any exit cost. */
  full: boolean;
}

/**
 * The served withdrawable now figures (API 1.155.0), accepted as ONE
 * source: every number the Risk & liquidity screen and the health score
 * show on this basis is read from here, never from a served aggregate.
 */
export interface AcceptedWithdrawableNow {
  /** The served T+0 (net of exit costs), which its positions add up to. */
  totalUsd: string;
  /** What the priced positions hold (see `listedValueUsd`). */
  pricedUsd: string;
  /**
   * totalUsd ÷ pricedUsd in basis points, floored and at most 100%: the
   * two figures shown beside it, so the three always agree.
   */
  shareBps: number | null;
  /**
   * Every position priced, and all of each can leave now (an exit cost is
   * paid, not waited for).
   */
  fullyWithdrawable: boolean;
  /** Per priced, listed position, by lowercase vault address. */
  byVault: ReadonlyMap<string, AcceptedPosition>;
}

/** USD figures as exact integers: 10^-8 dollars, never float sums. */
const USD_SCALE = 8;
const ONE_USD = 10n ** BigInt(USD_SCALE);
/** Half a cent, in those units: one served figure's rounding. */
const HALF_CENT = 500_000n;
const ONE_CENT = 2n * HALF_CENT;
const EXIT_COST = 'exit_cost';

function usdUnits(value: string): bigint | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (match == null) return null;
  const fraction = (match[2] ?? '').slice(0, USD_SCALE).padEnd(USD_SCALE, '0');
  return BigInt(match[1]!) * ONE_USD + BigInt(fraction);
}

/** Back to the decimal string every USD formatter reads. */
function usdString(units: bigint): string {
  const fraction = (units % ONE_USD).toString().padStart(USD_SCALE, '0');
  return `${units / ONE_USD}.${fraction}`;
}

/** Σ decimal USD strings, exactly; null if one does not read. */
export function sumUsd(values: readonly string[]): string | null {
  let sum = 0n;
  for (const value of values) {
    const units = usdUnits(value);
    if (units == null) return null;
    sum += units;
  }
  return usdString(sum);
}

/** part ÷ whole in basis points, floored; null when whole is not positive. */
export function shareOfBps(part: string, whole: string): number | null {
  const top = usdUnits(part);
  const bottom = usdUnits(whole);
  if (top == null || bottom == null || bottom <= 0n) return null;
  return Number((top * 10_000n) / bottom);
}

const within = (left: bigint, right: bigint, tolerance: bigint) =>
  (left > right ? left - right : right - left) <= tolerance;

/** The ledger's safety ceiling on the positions one response lists. */
const POSITION_CEILING = 100;

/**
 * The list may stop short of what is held, by the same rule the liquidity
 * view discloses: a served total says so; with none (or one smaller than
 * the list, which says nothing) a list at the ceiling cannot be shown to be
 * whole, so it is not taken as the wallet.
 */
export function mayBeCutShort(
  liquidity: Pick<WithdrawableNowTotalInput, 'positions' | 'totalPositions'>
): boolean {
  const listed = liquidity.positions.length;
  const total = liquidity.totalPositions;
  return total != null && total >= listed
    ? total > listed
    : listed >= POSITION_CEILING;
}

/**
 * What the listed priced positions hold, summed exactly, as a decimal
 * string; null if one does not read. Never a served aggregate: on a list
 * cut short it is the listed part only, and the screen says so.
 */
export function listedValueUsd(
  positions: WithdrawableNowTotalInput['positions']
): string | null {
  let sum = 0n;
  for (const position of positions) {
    if (position.valueUsd == null) continue;
    const value = usdUnits(position.valueUsd);
    if (value == null) return null;
    sum += value;
  }
  return usdString(sum);
}

const LABEL = 'investor liquidity';

/** A served aggregate its positions contradict: reported once, refused. */
function broken(path: string): null {
  reportBrokenField(LABEL, path);
  return null;
}

/**
 * The withdrawable now figures, accepted only when every served aggregate
 * agrees with the positions it is built from, as rowa-backend 1.155.0
 * builds them (#506): each USD figure is an unrounded figure rounded once
 * to cents, totals are summed unrounded and rounded once, and min() runs
 * on the unrounded values. So over n positions:
 *
 * - T+0 = Σ priced min(value, withdrawable), within n×½¢ + ½¢;
 * - the worst case is T+0 exactly, and covered is T+0 exactly;
 * - the total = Σ priced values, within n×½¢ + ½¢;
 * - uncovered = Σ priced values − covered within n×½¢ + 1¢, and = total −
 *   covered within 1¢;
 * - a figure carries no reason (a reason means "no figure"), and is the
 *   raw `maxWithdraw` read converted, so never without that read.
 *
 * When the list is cut short (the ledger's ceiling) the served sums reach
 * positions it does not list, so nothing shown beside them could account
 * for them: the served path is not accepted, and that is no break either.
 *
 * Not breaks, and never reported: a null T+0 (a position unmeasured, or
 * its live balance not the indexed one), a priced position with no figure,
 * or one marked `withdrawable_now_unmeasured`. The served path is just not
 * accepted. An unpriced position is out of every sum, figure or not.
 *
 * Any break refuses the whole served path, so the screen reads the vaults
 * itself, and is reported once by field, never by value.
 */
export function acceptedWithdrawableNow(
  liquidity: WithdrawableNowTotalInput
): AcceptedWithdrawableNow | null {
  if (!isWithdrawableNowBasis(liquidity.basis)) return null;
  const { typical, worstCase } = liquidity.immediateValueUsd;
  if (typical == null) return null;
  const total = usdUnits(typical);
  if (total == null) return null;
  // Truncation is legitimate (past the ledger's ceiling): read the listed
  // vaults directly, as in any other fallback, and report nothing.
  if (mayBeCutShort(liquidity)) return null;

  const byVault = new Map<string, AcceptedPosition>();
  let priced = 0n;
  let covered = 0n;
  let figures = 0n;
  let fullyWithdrawable = true;
  // One row per vault, priced or not: a second row for the same vault would
  // sum into the totals, or sit in both the read and the served sets, while
  // the per vault figure kept only one of them.
  const seen = new Set<string>();
  for (const position of liquidity.positions) {
    const vaultKey = position.vaultAddress.toLowerCase();
    if (seen.has(vaultKey)) return broken('positions.vaultAddress');
    seen.add(vaultKey);
    if (position.valueUsd == null) {
      // Unpriced: out of every sum, and nothing about it is "all of it".
      fullyWithdrawable = false;
      continue;
    }
    const served = position.withdrawableNow;
    // Unmeasured, or its live balance is not the indexed one: no figure
    // of it is trusted, and that is no break.
    if (
      served?.valueUsd == null ||
      position.uncoveredReason === 'withdrawable_now_unmeasured'
    ) {
      return null;
    }
    if (served.reason != null) {
      return broken('positions.withdrawableNow.reason');
    }
    // A USD figure is the raw read converted: none without the read.
    if (served.maxWithdrawRaw == null) {
      return broken('positions.withdrawableNow.maxWithdrawRaw');
    }
    const value = usdUnits(position.valueUsd);
    const withdrawable = usdUnits(served.valueUsd);
    if (value == null || withdrawable == null) return null;
    // Nothing read, so nothing to convert: a USD figure above zero beside
    // a raw read of zero contradicts it.
    if (served.maxWithdrawRaw === 0n && withdrawable > 0n) {
      return broken('positions.withdrawableNow.valueUsd');
    }
    const capped = withdrawable < value ? withdrawable : value;
    const gap = value - capped;
    const cost = position.uncoveredReason === EXIT_COST ? gap : 0n;
    const wait = gap - cost;
    priced += value;
    covered += capped;
    figures += 1n;
    const full = wait <= HALF_CENT;
    if (!full) fullyWithdrawable = false;
    byVault.set(vaultKey, {
      withdrawableUsd: usdString(capped),
      waitUsd: usdString(wait),
      costUsd: usdString(cost),
      none: capped <= HALF_CENT,
      full
    });
  }
  // No priced position: T+0 is null by contract (see the schema).
  if (figures === 0n) return null;

  const sumTolerance = figures * HALF_CENT + HALF_CENT;
  if (!within(total, covered, sumTolerance)) {
    return broken('immediateValueUsd.typical');
  }
  if (worstCase == null || usdUnits(worstCase) !== total) {
    return broken('immediateValueUsd.worstCase');
  }
  if (usdUnits(liquidity.coveredValueUsd) !== total) {
    return broken('coveredValueUsd');
  }
  const servedTotal = usdUnits(liquidity.totalValueUsd);
  if (servedTotal == null || !within(servedTotal, priced, sumTolerance)) {
    return broken('totalValueUsd');
  }
  const servedUncovered = usdUnits(liquidity.uncoveredValueUsd);
  if (
    servedUncovered == null ||
    !within(servedUncovered, servedTotal - total, ONE_CENT) ||
    !within(servedUncovered, priced - total, figures * HALF_CENT + ONE_CENT)
  ) {
    return broken('uncoveredValueUsd');
  }

  return {
    totalUsd: typical,
    pricedUsd: usdString(priced),
    shareBps:
      priced > 0n ? Math.min(10_000, Number((total * 10_000n) / priced)) : null,
    fullyWithdrawable,
    byVault
  };
}
